import React, { useCallback, useEffect, useState } from "react";
import { Group, Badge, ActionIcon, Text } from "@mantine/core";
import { MdRefresh } from "react-icons/md";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from "recharts";
import { useMsal } from "@azure/msal-react";

import { getToken, useLogin } from "../../authConfig";
import { getConversationsAnalyticsApi, ConversationDataPoint } from "../../api";
import {
    AggregationToggleGroup,
    AggregationValue,
    ChartScaffold,
    DateRangeControl,
    CountryFilterControl,
    DomainFilterControl,
    TouchpointFilterControl,
    createDefaultDateRange,
    formatDateRange,
    BarVisibilityToggleGroup,
    DEFAULT_BAR_VISIBILITY_OPTIONS
} from "./shared";
import { TimezoneIndicator } from "../common/TimezoneIndicator";
import { TimezoneUtils } from "../../utils/timezoneUtils";

export interface ConversationsChartProps {
    title?: string;
    height?: number;
    showTitle?: boolean;
    showLegend?: boolean;
    showStats?: boolean;
    defaultAggregation?: AggregationValue;
    defaultVisibleBars?: string[];
    entraOid?: string; // Filter for specific user
    hideUniqueUsers?: boolean; // Hide unique users option when on user detail page
    defaultDaysBack?: number; // Number of days back for default date range
    colors?: {
        total?: string;
        unique_users?: string;
        messages?: string;
    };
}

export const ConversationsChart: React.FC<ConversationsChartProps> = ({
    title = "Conversation Analytics",
    height = 400,
    showTitle = true,
    showLegend = true,
    showStats = true,
    defaultAggregation = "day",
    defaultVisibleBars = ["unique_users"], // Default: conversations + unique users
    entraOid,
    hideUniqueUsers = false,
    defaultDaysBack = 7,
    colors = {
        total: "#339af0",
        unique_users: "#ff8cc8",
        messages: "#51cf66"
    }
}) => {
    const [data, setData] = useState<ConversationDataPoint[]>([]);
    const [totalUniqueUsersFromAPI, setTotalUniqueUsersFromAPI] = useState<number>(0);
    const [totalMessagesFromAPI, setTotalMessagesFromAPI] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Filter default visible bars if hideUniqueUsers is true
    const filteredDefaultVisibleBars = hideUniqueUsers ? defaultVisibleBars.filter(bar => bar !== "unique_users") : defaultVisibleBars;

    const [visibleBars, setVisibleBars] = useState<string[]>(filteredDefaultVisibleBars);
    const [availableCountries, setAvailableCountries] = useState<string[]>([]);
    const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
    const [availableDomains, setAvailableDomains] = useState<string[]>([]);
    const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
    const [availableTouchpoints, setAvailableTouchpoints] = useState<string[]>([]);
    const [selectedTouchpoints, setSelectedTouchpoints] = useState<string[]>([]);
    const [sqlQuery, setSqlQuery] = useState<string | undefined>();

    const client = useLogin ? useMsal().instance : undefined;

    const [aggregation, setAggregation] = useState<AggregationValue>(defaultAggregation);
    const [dateRange, setDateRange] = useState<[Date, Date]>(() => createDefaultDateRange(defaultDaysBack));

    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) {
                throw new Error("No idToken available for fetching analytics");
            }

            const [start, end] = dateRange;
            const userTimezone = TimezoneUtils.getUserTimezone();
            const startDate = TimezoneUtils.formatDateForAPI(start);
            const endDate = TimezoneUtils.formatDateForAPI(end);
            const countries = selectedCountries.length > 0 ? selectedCountries : undefined;
            const domains = selectedDomains.length > 0 ? selectedDomains : undefined;
            const touchpoints = selectedTouchpoints.length > 0 ? selectedTouchpoints : undefined;
            const analytics = await getConversationsAnalyticsApi(
                idToken,
                aggregation,
                startDate,
                endDate,
                userTimezone,
                countries,
                touchpoints,
                entraOid,
                domains
            );
            setData(analytics.data);
            setTotalUniqueUsersFromAPI(analytics.total_unique_users || 0);
            setTotalMessagesFromAPI(analytics.total_messages || 0);
            setSqlQuery(analytics.sql_query ?? undefined);

            // Update available countries and touchpoints from the analytics response
            if (analytics.available_countries) {
                setAvailableCountries(analytics.available_countries);
            }
            setAvailableDomains(analytics.available_domains || []);
            if (analytics.available_touchpoints) {
                setAvailableTouchpoints(analytics.available_touchpoints);
            }
        } catch (chartError) {
            setError(chartError instanceof Error ? chartError.message : "Failed to load conversations data");
            setData([]);
            setTotalUniqueUsersFromAPI(0);
            setTotalMessagesFromAPI(0);
            setSqlQuery(undefined);
        } finally {
            setIsLoading(false);
        }
    }, [aggregation, client, dateRange, selectedCountries, selectedDomains, selectedTouchpoints, entraOid]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const totalConversations = data.reduce((sum, item) => sum + item.total, 0);

    const stats =
        showStats && totalConversations > 0 ? (
            <Group gap="xs">
                <Badge color="blue" variant="light" size="sm">
                    {totalConversations} Conversations
                </Badge>
                {visibleBars.includes("unique_users") && (
                    <Badge color="pink" variant="light" size="sm">
                        {totalUniqueUsersFromAPI} Unique Users
                    </Badge>
                )}
                {visibleBars.includes("messages") && (
                    <Badge color="green" variant="light" size="sm">
                        {totalMessagesFromAPI} Messages
                    </Badge>
                )}
            </Group>
        ) : null;

    const controls = (
        <Group gap={6} style={{ flexWrap: "nowrap" }}>
            <AggregationToggleGroup value={aggregation} onChange={setAggregation} disabled={!!error} />
            <DateRangeControl value={dateRange} onChange={setDateRange} disabled={!!error} />
            <CountryFilterControl
                value={selectedCountries}
                onChange={setSelectedCountries}
                availableCountries={availableCountries}
                disabled={!!error}
                loading={isLoading}
            />
            <DomainFilterControl
                value={selectedDomains}
                onChange={setSelectedDomains}
                availableDomains={availableDomains}
                disabled={!!error}
                loading={isLoading}
            />
            <TouchpointFilterControl
                value={selectedTouchpoints}
                onChange={setSelectedTouchpoints}
                availableTouchpoints={availableTouchpoints}
                disabled={!!error}
                loading={isLoading}
            />
            <ActionIcon variant="light" size="sm" onClick={loadData} loading={isLoading} title="Refresh data" disabled={!!error}>
                <MdRefresh size={16} />
            </ActionIcon>
        </Group>
    );

    // Check if any filters are applied (different from default state)
    const defaultDateRange = createDefaultDateRange();
    const isDateRangeModified = dateRange[0].getTime() !== defaultDateRange[0].getTime() || dateRange[1].getTime() !== defaultDateRange[1].getTime();
    const isVisibleBarsModified = JSON.stringify(visibleBars) !== JSON.stringify(defaultVisibleBars);
    const hasFiltersApplied =
        aggregation !== "day" || isDateRangeModified || selectedCountries.length > 0 || selectedDomains.length > 0 || isVisibleBarsModified;

    const handleResetFilters = () => {
        setAggregation("day");
        setDateRange(createDefaultDateRange(defaultDaysBack));
        setSelectedCountries([]);
        setSelectedDomains([]);
        setVisibleBars(filteredDefaultVisibleBars);
    };

    // Filter bar visibility options based on hideUniqueUsers prop
    const barVisibilityOptions = hideUniqueUsers
        ? DEFAULT_BAR_VISIBILITY_OPTIONS.filter(option => option.key !== "unique_users")
        : DEFAULT_BAR_VISIBILITY_OPTIONS;

    const info = (
        <Group justify="space-between" align="center">
            <BarVisibilityToggleGroup selectedBars={visibleBars} onChange={setVisibleBars} disabled={!!error} options={barVisibilityOptions} />
            <Group gap="xs">
                <Text size="xs" c="dimmed">
                    {formatDateRange(dateRange)}
                </Text>
                <TimezoneIndicator size="xs" />
            </Group>
        </Group>
    );

    return (
        <ChartScaffold
            title={title}
            showTitle={showTitle}
            loading={isLoading}
            error={error}
            empty={!error && data.length === 0}
            emptyMessage="No conversation data found for the selected time range."
            stats={stats}
            controls={controls}
            info={info}
            height={height}
            onRefresh={loadData}
            onResetFilters={handleResetFilters}
            showResetFilters={hasFiltersApplied}
            infoContent="Shows conversation volumes and message counts over time. Track user engagement, identify usage patterns, and monitor system activity trends."
            sqlQuery={sqlQuery}
            chartData={data}
            showDeveloperTools={true}
        >
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="name"
                        tick={{ fontSize: 12 }}
                        angle={data.length > 10 ? -45 : 0}
                        textAnchor={data.length > 10 ? "end" : "middle"}
                        height={data.length > 10 ? 60 : 30}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                        formatter={(value, name) => {
                            const label = (name as string) ?? "";
                            const scalar = Array.isArray(value) ? (value as (number | string)[])[0] : (value as number | string);
                            const numericValue = typeof scalar === "number" ? scalar : Number(scalar ?? 0);

                            if (label === "total") {
                                return [numericValue, "Conversations"];
                            }
                            if (label === "unique_users") {
                                return [numericValue, "Unique Users"];
                            }
                            if (label === "messages") {
                                return [numericValue, "Messages"];
                            }
                            return [scalar ?? numericValue, label];
                        }}
                        contentStyle={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 4 }}
                    />
                    {showLegend && <Legend />}
                    <Bar dataKey="total" fill={colors.total} name="Conversations" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="total" position="top" style={{ fontSize: "12px", fontWeight: 600 }} />
                    </Bar>
                    {visibleBars.includes("unique_users") && (
                        <Bar dataKey="unique_users" fill={colors.unique_users} name="Unique Users" radius={[4, 4, 0, 0]}>
                            <LabelList dataKey="unique_users" position="top" style={{ fontSize: "12px", fontWeight: 600 }} />
                        </Bar>
                    )}
                    {visibleBars.includes("messages") && (
                        <Bar dataKey="messages" fill={colors.messages} name="Messages" radius={[4, 4, 0, 0]}>
                            <LabelList dataKey="messages" position="top" style={{ fontSize: "12px", fontWeight: 600 }} />
                        </Bar>
                    )}
                </BarChart>
            </ResponsiveContainer>
        </ChartScaffold>
    );
};
